import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import { AuthenticationApi } from "../../../API/Authentication/LoginApi";
import {
  DashboardFromRoleArray,
  DashboardFromRoleResponse,
} from "../../../@Types/DRO/ChurnApiResponse/LoginResponse";
import { motion, AnimatePresence } from "framer-motion";
const DashboardSelection: React.FC = () => {
  const [dashboards, setDashboards] = useState<DashboardFromRoleArray[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        const role = localStorage.getItem("authority");
        if (!role) {
          setError("Role not found");
          setLoading(false);
          return;
        }
        const response: DashboardFromRoleResponse =
          await AuthenticationApi.api.getDashboardFromRole(role);
        if (response.code === 2000) {
          const dashboardData = response.data;
          if (dashboardData.length === 1) {
            // Directly navigate if only one dashboard is available
            handleDashboardClick(dashboardData[0].url);
          } else {
            setDashboards(dashboardData);
            setLoading(false);
          }
        } else {
          setError("Failed to fetch dashboards");
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching dashboards:", error);
        setError("Network error occurred");
        setLoading(false);
      }
    };

    fetchDashboards();
  }, []);

  const handleDashboardClick = (url: string) => {
    const cleanedUrl = url.startsWith("/") ? url : `/${url}`;
    window.location.href = `${window.location.origin}${cleanedUrl}`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{ mb: 4, mt: 8, color: "black", fontWeight: "bold" }}>
        Select the Dashboard to Navigate.
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        <AnimatePresence>
          {dashboards.map((dashboard, index) => (
            <Grid item key={dashboard.id} xs={12} sm={6} md={4} lg={3}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "black",
                    color: "white",
                    boxShadow: 3,
                    borderRadius: 4,
                    overflow: "hidden",
                    "&:hover": {
                      backgroundColor: "green",
                      transform: "scale(1.02)",
                      transition:
                        "transform 0.3s ease-in-out, background-color 0.3s ease-in-out",
                    },
                  }}>
                  <CardActionArea
                    onClick={() => handleDashboardClick(dashboard.url)}
                    sx={{ flexGrow: 1 }}>
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{ fontWeight: "bold" }}>
                        {dashboard.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Click to navigate
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Box>
  );
};

export default DashboardSelection;